import { useState } from 'react';
import { Badge, Box, Divider, Grid, Group, Stack, Text, ThemeIcon, Tooltip, UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconUserCircle } from '@tabler/icons-react';
import { getReferenceString } from '@medplum/core';
import { EligibilityFormTimelineGroup } from '@/components/Timeline/types';
import { HighlightableItem } from '@/components/Timeline/TimelineItems/HighlightableItem';
import { TimelineCard } from '@/components/Timeline/TimelineItems/TimelineCard';
import {
  EligibilityCollapsedField,
  getEligibilityCollapsedFields,
  getEligibilityGroups,
  isEmployerCoverageForm,
} from '@/services/Timeline/eligibilityForm';
import { OFFERING_COLORS, Offering } from '@/pages/PatientTimelinePage/PatientTimelineHeader/offeringColors';
import { DateTimeTooltip } from './DateTimeTooltip';

interface EligibilityFormTimelineItemProps {
  group: EligibilityFormTimelineGroup;
  highlightRef?: string | null;
}

const LABEL_WIDTH = 132;

/**
 * Eligibility form completed on everself.com (BAR-2495), or the employer
 * coverage form's version of it (BAR-2836).
 *
 * Collapsed shows exactly three fields — final action, then nearest location and
 * interested in (the employer coverage form: employer and benefit check) — so a
 * 40-card timeline still scans at about a second per card. Everything else sits
 * behind the centre-on-line *Show more* chip.
 */
export function EligibilityFormTimelineItem({ group, highlightRef }: EligibilityFormTimelineItemProps) {
  const [expanded, setExpanded] = useState(false);

  const item = group.items[0];
  const itemRef = item ? getReferenceString(item.reference) : undefined;
  const questionnaireResponse = item?.questionnaireResponse;

  const collapsedFields = getEligibilityCollapsedFields(questionnaireResponse);
  const leftGroups = getEligibilityGroups(questionnaireResponse, 'left');
  const rightGroups = getEligibilityGroups(questionnaireResponse, 'right');
  const hasExpandableContent = leftGroups.length > 0 || rightGroups.length > 0;

  return (
    <HighlightableItem itemRef={itemRef!} highlightRef={highlightRef}>
      <TimelineCard header={<EligibilityFormHeader group={group} />}>
        <Stack gap="xs">
          {collapsedFields.map((field) => (
            <FieldRow key={field.label} field={field} />
          ))}

          {hasExpandableContent && (
            <>
              {expanded && (
                <Grid gutter="xl" pt="xs">
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Stack gap="lg">
                      {leftGroups.map((eligibilityGroup) => (
                        <FieldGroup key={eligibilityGroup.linkId} group={eligibilityGroup} />
                      ))}
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Stack gap="lg">
                      {rightGroups.map((eligibilityGroup) => (
                        <FieldGroup key={eligibilityGroup.linkId} group={eligibilityGroup} />
                      ))}
                    </Stack>
                  </Grid.Col>
                </Grid>
              )}

              <Divider
                labelPosition="center"
                label={
                  <UnstyledButton
                    onClick={() => setExpanded((value) => !value)}
                    aria-expanded={expanded}
                    style={{ borderRadius: 999 }}
                  >
                    <Group
                      gap={4}
                      align="center"
                      px="sm"
                      py={4}
                      style={{ border: '1px solid #e9ecef', borderRadius: 999, backgroundColor: '#ffffff' }}
                    >
                      <Text size="xs" c="dimmed" fw={500}>
                        {expanded ? 'Show less' : 'Show more'}
                      </Text>
                      {expanded ? (
                        <IconChevronUp size={13} color="#868e96" />
                      ) : (
                        <IconChevronDown size={13} color="#868e96" />
                      )}
                    </Group>
                  </UnstyledButton>
                }
              />
            </>
          )}
        </Stack>
      </TimelineCard>
    </HighlightableItem>
  );
}

function FieldGroup({ group }: { group: { label: string; fields: EligibilityCollapsedField[] } }) {
  return (
    <Stack gap={6}>
      <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: 0.4 }}>
        {group.label}
      </Text>
      {group.fields.map((field) => (
        <FieldRow key={field.label} field={field} />
      ))}
    </Stack>
  );
}

function FieldRow({ field }: { field: EligibilityCollapsedField }) {
  return (
    <Group gap="xs" wrap="nowrap" align="baseline">
      <Text size="sm" c="dimmed" style={{ minWidth: LABEL_WIDTH, flexShrink: 0 }}>
        {field.label}
      </Text>
      <FieldValue field={field} />
    </Group>
  );
}

/**
 * Derived values carry a dotted underline and a tooltip naming their source, so
 * CI never mistakes our inference for the patient's own words. Derived-ness is
 * data on the resource, never a hardcoded list here.
 */
function FieldValue({ field }: { field: EligibilityCollapsedField }) {
  const offeringColor = OFFERING_COLORS[field.value as Offering];

  const value = offeringColor ? (
    <Badge
      size="sm"
      radius="sm"
      variant="filled"
      styles={{ root: { backgroundColor: offeringColor, color: '#FAFCF7' } }}
    >
      {field.value}
    </Badge>
  ) : (
    <Text size="sm">{field.value}</Text>
  );

  if (!field.derivedFrom) {
    return value;
  }

  return (
    <Tooltip label={field.derivedFrom} position="top" withArrow multiline maw={280}>
      <Box
        style={{
          borderBottom: '1px dotted #adb5bd',
          cursor: 'help',
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        {value}
      </Box>
    </Tooltip>
  );
}

function EligibilityFormHeader({ group }: { group: EligibilityFormTimelineGroup }) {
  // The title is the one place the card says which form the lead came in on.
  const form = isEmployerCoverageForm(group.items[0]?.questionnaireResponse)
    ? 'Employer coverage form'
    : 'Eligibility form';

  return (
    <Group justify="space-between" align="center">
      <Text size="md" fw={700}>
        {form} completed on everself.com
      </Text>
      <Group gap="md" align="center">
        {/* Patient-initiated: they filled this in themselves with nobody on our
            team involved, so the actor avatar is unnamed — no persona. */}
        <Tooltip label="Completed by the patient on everself.com" position="top" withArrow>
          <ThemeIcon variant="light" radius="xl" size={30} color="indigo" aria-label="Patient-initiated" role="img">
            <IconUserCircle size={18} />
          </ThemeIcon>
        </Tooltip>
        <DateTimeTooltip date={group.timestamp} />
      </Group>
    </Group>
  );
}
